// Pure top-level draw orchestration for the mahjong table. Owns no state —
// given a `MahjongState` snapshot + layout + palette it composes the table,
// pond, four seats' hands + melds, and the seat compass. Lives apart from
// GameScene so that file stays glue and this stays a single readable pass.

import type { MahjongState } from '../game/GameState.js';
import { DEALER } from '../game/stateCore.js';
import { SEATS, type Seat } from '../game/types.js';
import type { MatchState } from '../match/MatchState.js';
import { seatPosition, type LayoutConfig } from '../layout/types.js';
import { drawDiscardPond } from '../render/DiscardRenderer.js';
import { drawLocalHand, drawOpponentHand, drawOpponentHandFaceUp } from '../render/HandRenderer.js';
import { drawCompass, drawTable } from '../render/TableRenderer.js';
import { drawResetButton, layoutResetButton } from '../render/ViewportControls.js';
import { drawMatchHud } from '../ui/MatchHud.js';
import { shouldRevealHand } from './handReveal.js';
import type { RenderPalette } from '../render/palette.js';
import type { Viewport } from '../render/Viewport.js';
import { t } from '../i18n/I18n.js';
import { NUMERAL_FONT_STACK, WALL_COUNT_FONT_PX } from '../utils/constants.js';
import { wallCountOrigin } from './wallCountOrigin.js';

/**
 * Single frame: felt + pond floor (SCREEN space) → the discard pond's TILES
 * and each seat's called MELDS, clipped to the pond rect and panned/zoomed
 * by `viewport` (WORLD space) → all four racks' concealed tiles + compass +
 * wall count (SCREEN space, drawn LAST so they always paint OVER any panned/
 * zoomed pond content that drifts under a rack — canvas has no z-index, only
 * draw order). This mirrors domino precisely, clip included: the board and
 * racks never move, only the tiles actually sitting in the play zone (there,
 * the chain; here, the pond) do, and panning can never bleed them past that
 * zone's edges — see `render/Viewport.ts` and domino's `chainRegion` clip in
 * `main.ts`. A called meld renders "under" its seat's discards (see
 * `render/tileLayout.splitPondCell`) rather than beside the concealed hand —
 * it's an open, ON-THE-TABLE set, not part of the rack.
 *
 * `localSeat` is the point of view: that seat's rack draws face-up at the
 * bottom edge and every other seat rotates around it (see `seatPosition`).
 * Solo play and the lessons pass seat 0, the identity case.
 *
 * `pillBottom` is the invite pill's bottom edge in canvas space, or null while
 * it isn't showing — see `wallCountOrigin`.
 */
export function renderScene(
  ctx: CanvasRenderingContext2D,
  state: MahjongState,
  layout: LayoutConfig,
  palette: RenderPalette,
  viewport: Viewport,
  matchState: MatchState | null,
  localSeat: Seat,
  pillBottom: number | null,
  highlightTileIds?: readonly number[],
): void {
  drawTable(ctx, layout, palette);

  ctx.save();
  // Clip to the pond BEFORE the viewport transform — the rect must be in
  // screen space (the pond's actual on-screen rect), not world space, or
  // panning would drag the clip window around along with the tiles.
  ctx.beginPath();
  ctx.rect(layout.pond.x, layout.pond.y, layout.pond.width, layout.pond.height);
  ctx.clip();
  viewport.applyTo(ctx);
  drawDiscardPond(ctx, layout, state.discards, state.melds, palette, localSeat);
  ctx.restore();

  // At hand-end concealed hands are revealed face-up so the owner can study the
  // completed shapes; during play only the local rack shows its faces. Online,
  // a seat the server never revealed stays face-down — see `shouldRevealHand`.
  for (const seat of SEATS) {
    const zone = layout.seatZones[seatPosition(seat, localSeat)];
    const hand = state.hands[seat] ?? [];

    if (seat === localSeat) {
      drawLocalHand(ctx, zone, hand, palette, {
        drawnTileId: state.drawnTile?.id ?? null,
        highlightTileIds,
      });
    } else if (shouldRevealHand(state.phase, hand)) {
      drawOpponentHandFaceUp(ctx, zone, hand, palette, zone.rotation);
    } else {
      drawOpponentHand(ctx, zone, hand.length, palette, zone.rotation);
    }
  }

  // Simplified never rotates the dealer off `DEALER`, so a hand with no match
  // wrapper reads the same winds it always did.
  drawCompass(ctx, layout, palette, state.turn, localSeat, matchState?.dealer ?? DEALER);
  drawWallCount(ctx, layout, palette, state.wall.length, pillBottom);
  drawResetButton(ctx, layoutResetButton(layout.pond), palette, viewport.isUserControlled());
  if (matchState !== null) drawMatchHud(ctx, layout, palette, matchState, localSeat);
}

/** Small live readout of the tiles remaining in the wall, top-left of the felt. */
function drawWallCount(
  ctx: CanvasRenderingContext2D,
  layout: LayoutConfig,
  palette: RenderPalette,
  count: number,
  pillBottom: number | null,
): void {
  const origin = wallCountOrigin(layout.table, pillBottom);
  ctx.save();
  ctx.font = `${WALL_COUNT_FONT_PX}px ${NUMERAL_FONT_STACK}`;
  ctx.fillStyle = palette.compassText;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';
  ctx.fillText(t('game.wallCount', { count }), origin.x, origin.y);
  ctx.restore();
}
